import { useDynamicTranslation, useTranslation } from '@thoughtspot/i18n';
import { collectEvent, UserAction, userActionTypeEnum, } from '@thoughtspot/metrics';
import React, { useMemo } from 'react';
import { MutedAlert } from '../../alert/muted/muted-alert';
import { Button } from '../../button';
import { IconID } from '../../icon';
import { Vertical } from '../../layout';
import { Pagination } from '../../pagination';
import { getPaginationOptimizedDotsProps } from '../../pagination/pagination-dots/pagination-dots.util';
import { SearchBar } from '../../search-bar';
import { Tab, TabHeaderHorizontalAlignment, TabTrayLayout, TabTraySize, } from '../../tab';
import { ObjectTable } from '../../table/object-table/object-table';
import { ColumnWidth, ColumnWidthType as ObjectTableColumnWidthType, RowHeightType as ObjectTableRowHeightType } from '../../table/object-table/object-table.interface';
import { Typography } from '../../typography';
import { ListTableInitialEmptyState } from './components/ListTableInitialEmptyState';
import { useScrollToTop } from './hooks/useScrollToTop';
import styles from './ListTable.module.scss';
import { extractColumnName, wrapActionGroupsWithAnalytics, } from './utils/list-table.util';
// Constants for pagination fallback values
const DEFAULT_PAGINATION_OFFSET = 0;
const DEFAULT_PAGINATION_BATCH_SIZE = 20;
const ListTable = ({ columns, data: rawData, loading = false, selectedRowsIds = [], onRowsSelected, headerHeight = 32, hasAutoHeightColumns = true, agGridContext = {}, onSortingChanged, onRowClick, isRowClickable, rowMenu, suppressRowClickSelection = true, rowSelection = 'multiple', enableCellTextSelection = true, ensureDomOrder = true, suppressRowVirtualisation = true, suppressCellVirtualisation = true, suppressKeyboardEvent, title, tabs, search, searchBarPrefix, searchBarExtras, actionButtons = [], actionBar, alert, error, selectedRowActions, noDataProps, modals, pagination, enableScrollToTop = false, enableTranslation = false, 'data-testid': dataTestId, }) => {
    const { t } = useTranslation();
    const { dt } = useDynamicTranslation();
    // Apply translation if enabled (runs on every render for dynamic updates)
    const data = enableTranslation && rawData && rawData.length > 0
        ? rawData.map((obj) => {
            var _a, _b, _c;
            if (!obj ||
                (!((_a = obj.primaryName) === null || _a === void 0 ? void 0 : _a.primaryText) &&
                    !((_b = obj.liveboardName) === null || _b === void 0 ? void 0 : _b.text))) {
                return obj;
            } // for liveboard schdule page
            if ((_c = obj.liveboardName) === null || _c === void 0 ? void 0 : _c.text) {
                return {
                    ...obj,
                    liveboardName: {
                        ...obj.liveboardName,
                        text: dt(obj.liveboardName.text),
                    },
                };
            } // for liveboard-list, answer-list, home-library list page
            return {
                ...obj,
                primaryName: {
                    ...obj.primaryName,
                    primaryText: dt(obj.primaryName.primaryText),
                },
            };
        })
        : rawData;
    // Handle column header click for analytics tracking
    const handleColumnHeaderClick = (columnName) => {
        try {
            const userAction = new UserAction(userActionTypeEnum.TABLE_HEADER_CLICK);
            collectEvent(userAction, {
                filterColumn: columnName,
            });
        }
        catch (error) {
            // Fail silently if analytics tracking fails - don't break user experience
        }
    };
    // Handle sorting dropdown item clicks for analytics tracking
    const handleSortingAction = (sortBy, columnName) => {
        try {
            const userAction = new UserAction(userActionTypeEnum.TABLE_SORT_BY_CLICK);
            collectEvent(userAction, {
                sortBy,
                filterColumn: columnName,
            });
        }
        catch (error) {
            // Fail silently if analytics tracking fails - don't break user experience
        }
    };
    // Extract pagination values for static dependency analysis
    const paginationOffset = pagination === null || pagination === void 0 ? void 0 : pagination.offset;
    const paginationBatchSize = pagination === null || pagination === void 0 ? void 0 : pagination.batchSize;
    // Memoize the scroll dependencies to prevent unnecessary hook calls on re-renders
    const scrollDependencies = useMemo(() => {
        const deps = [];
        // Add pagination dependencies if they exist
        if (typeof paginationOffset === 'number') {
            deps.push(paginationOffset);
        }
        else {
            deps.push(DEFAULT_PAGINATION_OFFSET); // Fallback value
        }
        if (typeof paginationBatchSize === 'number') {
            deps.push(paginationBatchSize);
        }
        else {
            deps.push(DEFAULT_PAGINATION_BATCH_SIZE); // Fallback value
        }
        return deps;
    }, [paginationOffset, paginationBatchSize]);
    // Use scroll to top hook for pagination changes - memoized to prevent unnecessary calls
    const { containerRef } = useScrollToTop({
        enabled: enableScrollToTop,
        behavior: 'smooth',
        dependencies: scrollDependencies,
    });
    // Convert ColumnDefinition to ObjectTableColDef
    const tableColumns = columns.map((col) => {
        var _a;
        const columnName = extractColumnName(col.colDef);
        const existingActions = (_a = col.colDef.headerComponentParams) === null || _a === void 0 ? void 0 : _a.actions;
        return {
            columnWidthType: col.columnWidthType || ObjectTableColumnWidthType.Fluid,
            columnWidth: col.columnWidth || ColumnWidth.Medium,
            rowHeightType: col.rowHeightType || ObjectTableRowHeightType.Auto,
            colDef: {
                ...col.colDef,
                headerComponentParams: {
                    ...col.colDef.headerComponentParams,
                    onHeaderClick: handleColumnHeaderClick,
                    // Wrap existing actions with analytics tracking
                    ...(existingActions && {
                        actions: wrapActionGroupsWithAnalytics(existingActions, columnName, handleSortingAction),
                    }),
                },
            },
        };
    });
    const objectTableProps = {
        loading,
        hideLoader: true,
        data,
        columns: tableColumns,
        selectedRowsIds,
        onRowsSelected,
        agGridContext,
        headerHeight,
        hasAutoHeightColumns,
        onSortingChanged,
        onRowClick,
        isRowClickable,
        rowMenu,
        suppressRowClickSelection,
        rowSelection,
        enableCellTextSelection,
        ensureDomOrder,
        suppressRowVirtualisation,
        suppressCellVirtualisation,
        ...(suppressKeyboardEvent && {
            defaultColDef: {
                suppressKeyboardEvent,
            },
        }),
    };
    let paginationConfig = null;
    if (pagination) {
        // Check if pagination is already in the new format (has fetch property with all required fields)
        if (pagination.fetch &&
            typeof pagination.fetch === 'object' &&
            'offset' in pagination.fetch) {
            paginationConfig = getPaginationOptimizedDotsProps(pagination.offset, pagination.batchSize, pagination.previous, pagination.next, pagination.jumpTo, pagination.fetch.offset, pagination.fetch.objectsCount, pagination.fetch.isLastBatch, pagination.fetch.previous, pagination.fetch.next);
        }
        else {
            // If it's already in the new format, use it directly
            paginationConfig = pagination;
        }
    }
    const onGridReadyCallback = () => {
        // Grid is ready, no special pagination logic needed
    };
    let tableContent;
    if (error) {
        tableContent = (React.createElement(Vertical, { hAlignContent: "center", vAlignContent: "center", grow: true, className: styles.listTableErrorContainer, style: { minHeight: '60vh', width: '100%' } },
            React.createElement(MutedAlert, { imageVisible: true, title: t('error.validation.default'), description: error.message, isVisible: true, size: "small", primaryAction: error.onRetry
                    ? {
                        label: t('REFRESH'),
                        onClick: error.onRetry,
                    }
                    : undefined })));
    }
    else if (!loading && (!data || data.length === 0) && noDataProps) {
        // Check if we should show empty state only on initial empty (first page with no data)
        if (noDataProps.showOnlyOnInitialEmpty) {
            // Show a similar empty state to HomeLibraryEmptyState
            tableContent = (React.createElement(ListTableInitialEmptyState, { title: noDataProps.title, description: noDataProps.description, emptyRowsCount: 10 }));
        }
        else {
            // Show regular empty state
            tableContent = (React.createElement("div", { className: styles['list-table-empty-state'] },
                React.createElement(MutedAlert, { title: noDataProps.title, description: noDataProps.description, isVisible: true, global: true, errorCode: noDataProps.img, imageVisible: true, primaryAction: noDataProps.actionButton
                        ? {
                            label: noDataProps.actionButton.buttonTitle,
                            onClick: noDataProps.actionButton
                                .onButtonClick,
                        }
                        : undefined })));
        }
    }
    else {
        tableContent = (React.createElement(ObjectTable, { ...objectTableProps, onGridReadyCallback: onGridReadyCallback }));
    }
    // Generate selected row action bar
    const selectedRowActionBar = selectedRowsIds &&
        selectedRowsIds.length > 0 &&
        selectedRowActions &&
        selectedRowActions.length > 0 ? (React.createElement("div", { className: styles.actionButtonsContainer }, selectedRowActions.map((action) => (React.createElement(Button, { key: action.id, text: action.text, type: action.type || 'secondary', icon: action.icon, onClick: () => action.onClick(selectedRowsIds), "data-testid": action['data-testid'] }))))) : null;
    return (React.createElement("div", { ref: containerRef, className: styles['list-table-container'], "data-testid": dataTestId },
        title && (React.createElement("div", { className: styles['list-table-header'] },
            React.createElement(Typography, { variant: "page-title", className: styles['list-table-title'] }, title),
            tabs && selectedRowsIds && selectedRowsIds.length === 0 && (React.createElement(React.Fragment, null,
                React.createElement("div", { className: styles['list-table-separator'] }),
                React.createElement("div", { className: styles['list-table-tabs'] },
                    React.createElement(Tab, { layout: TabTrayLayout.Horizontal, size: TabTraySize.Small, selectedTabId: tabs.selectedTabId, forceUpdateTabs: true, headerAlignment: TabHeaderHorizontalAlignment.CENTER, className: styles.answerTabContainer, ...(tabs.tabProps || {}) }, tabs.items.map((tab) => (React.createElement(Tab.Item, { key: tab.id, id: tab.id, name: tab.name, onTabItemClick: tab.onClick, "data-testid": tab['data-testid'] },
                        React.createElement("div", null)))))))))),
        !error && (React.createElement("div", { className: styles['list-table-action-bar'] }, actionBar || (React.createElement(React.Fragment, null,
            (search || searchBarExtras || searchBarPrefix) && (React.createElement("div", { className: styles.searchBarContainer },
                searchBarPrefix && (React.createElement("span", { className: styles.searchBarPrefix }, searchBarPrefix)),
                search && (React.createElement(SearchBar, { searchPlaceholder: search.placeholder || '', onSearchChange: search.onChange, searchValue: search.value, suffixIcon: search.value.length > 0
                        ? IconID.CROSS
                        : undefined, onSuffixIconClick: () => search.onChange(''), onFocus: search.onFocus })),
                searchBarExtras && (React.createElement("div", { className: styles.searchBarExtras }, searchBarExtras)))),
            selectedRowActionBar ||
                (actionButtons.length > 0 && (React.createElement("div", { className: styles.actionButtonsContainer }, actionButtons.map((button) => (React.createElement(Button, { key: button.id, text: button.text, type: button.type ||
                        'secondary', icon: button.icon, onClick: button.onClick, isDisabled: button.isDisabled, tooltip: button.tooltip, "data-testid": button['data-testid'] })))))))))),
        React.createElement("div", { className: styles['list-table-content'] }, tableContent),
        alert && alert.isVisible && (React.createElement("div", { className: styles['list-table-alert'] },
            React.createElement(MutedAlert, { title: alert.message, isVisible: alert.isVisible, size: "small", primaryAction: alert.onClose
                    ? {
                        label: t('CLOSE'),
                        onClick: alert.onClose,
                    }
                    : undefined }))),
        paginationConfig && !loading && (React.createElement("div", { className: styles['list-table-pagination'] },
            React.createElement(Pagination.OptimizedDots, { offset: paginationConfig.offset, batchSize: paginationConfig.batchSize, previous: paginationConfig.previous, next: paginationConfig.next, jumpTo: paginationConfig.jumpTo, fetch: paginationConfig.fetch }))),
        modals));
};
export default ListTable;
//# sourceMappingURL=ListTable.js.map